<script setup lang="ts">

import Button from '@/components/Button.vue'

const props = withDefaults(defineProps<{
  service: {
    name: string,
    headline: string,
    description: string,
    keyPoints: string[],
    cta: string,
    slug: string,
    index: number,
    image: string
  }
}>(), {
  service: () => {
    return {
      name: 'Training',
      headline: 'Empower Your Staff',
      description: 'Empower your team with the knowledge and skills to maintain world-class hygiene standards every day.',
      keyPoints: [
        'Safe and effective use of products',
        'Hands-on demonstrations and best practices',
        'Personal hygiene and compliance training',
        'Cost-control awareness and efficiency'
      ],
      cta: 'Learn More',
      slug: 'training',
      index: 1,
      image: '/images/training.jpg'
    }
  },
})

const router = useRouter()
function redirect(slug: string) {
  router.push(`/services`)
}


const image = computed(() => props.service.image)

</script>

<template>
  <div class="service-feature-card">
    <div class="inner card">

      <div class="sec-title gp-1 flex">
        <div class="index text-gray-200">
          0{{ service.index }}.
        </div>
        <div class="title fs-15 fw-400 font-primary"> 
          {{ service.headline }}
        </div>
      </div>
      <div class="sec-image" >
        <div class="inner" :style="{ backgroundImage: `url(${image})` }" />
        <div class="v-line left" />
        <div class="sec-title bg-white z-2 absolute bottom-0 left-0 w-auto p-2 text-[var(--body-primary)]">
          {{ service.name }}
        </div>
      </div>

      <div class="sec-content max-w-87.5%! mx-auto">

        <div class="sec-description">
           {{ service.description }}
        </div>
        <div class="sec-key-points">
          <div v-for="keyPoint in service.keyPoints" :key="keyPoint" class="key-point flex items-center">
            <div class="icon">
              <i class="flex w-8 h-8 i-mdi:check" />
            </div>
            <div class="label text-[var(--body-primary)] fs--05">
              {{ keyPoint }}
            </div>
          </div>
        </div>
        <div class="sec-actions">
          <div class="action">
            <!-- <button @click="redirect(service.slug)" class="card-link">
              <div class="sec-label">
                {{ service.cta }}
              </div>
              <div class="sec-icon">
                <i class="i-mdi:arrow-right"></i>
              </div>
            </button> -->

            <Button :label="service.cta" class="card-button fs-0! flex g-0.5! gpx-0.75 gpy-0.33 items-center" :link="`/services`" >
            <div class="sec-label fs--05!">
              {{ service.cta }}
            </div>
            <div class="sec-icon">
              <i class="i-mdi:arrow-right fs--05!"></i>
            </div>
            </Button>
          </div>
        </div>
      </div>


    </div>

  </div>
</template>

<style lang="scss" scoped>
.service-feature-card {
  width: 100%;
  margin-bottom: gr(2);
  transform: scale(0.99);
  transition: all 0.2s ease;

  padding: 0 gr(1);

  @include mq($until: mlx) {
    padding: 0 gr(0.5);
  }


  >.inner {
    width: 100%;
    max-width: 90%;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    max-width: 440px;

    &.card {
      background: white;
      display: grid;
      overflow: hidden;
      border-radius: 0.5rem;
      @apply shadow-lg;

      >.sec-title {
        font-size: fs(1.5);

        font-weight: 500;
        max-width: 90%;
        line-height: gr(1.2);

        color: var(--base-700);

        gap: gr(0.5);
        padding: gr(1) gr(1) gr(0.5) gr(1);

        >.index {
          font-size: fs(1.5);
          font-weight: 500;
          color: var(--base-500);
        }

        >.title {
          font-size: fs(0);
          font-weight: 500;
          color: var(--base-650);
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

      }

      >.sec-image {
        width: 100%;
        height: 220px;
        position: relative;
        overflow: hidden;

        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        >.inner {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transform: translate(-50%, -50%) scale(1.0);
          transition: all 0.5s ease;
        }
        
        >.v-line {
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: white;
          z-index: 1;
          &.left {
            left: 0;
          }
          &.right {
            right: 0;
          }
        }



        >.sec-title {
          font-size: fs(0);
          font-weight: 500;
          color: var(--a-dark-blue);
          padding: gr(0.25) gr(1);
          border-radius: gr(0.25);  
          background: var(--base-100);
          box-shadow: 0 0 0 1px var(--base-200);
          margin: gr(0.5);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        
      }

      >.sec-content {
        max-width: calc(100% - gr(1.75));
        margin: 0 auto;
        padding: gr(0.75) 0;
        display: grid;
        gap: gr(0.75);

        >.sec-headline {
          font-size: fs(1.5);
          font-weight: 500;
          color: var(--body-primary);
        }

        >.sec-title {
          width: 100%;

          >.title {
            font-size: fs(1.5);

            font-weight: 500;
            max-width: 90%;
            line-height: gr(1.2);

            color: var(--base-700);

          }
        }

        >.sec-meta {
          width: 100%;
          display: none;

          >.meta {
            font-size: fs(-1);
            color: var(--base-600);
            font-weight: 400;

            >span {
              font-size: fs(-1);
            }
          }
        }

        >.sec-description {
          width: 100%;
          font-size: fs(0);
          color: var(--base-600);
          font-weight: 400;
          line-height: gr(1.25);
        }

        >.sec-key-points {

          display:grid;
          gap: gr(0.75);
          margin-bottom: gr(0.5);
          .key-point {  
            font-size: fs(-1);
            gap: gr(0.5);
            >.label {
              font-size: fs(-0.5);
              color: var(--base-650);
              line-height: gr(1);
            }
            >.icon {
              width: gr(1);
              height: gr(1);
              display: flex;
              align-items: center;
              justify-content: center;
              i {
                color: var(--green-500);
              }
            }
          }
        }

        >.sec-actions {
          display: flex;
          justify-content: flex-end;

          >.action {

            
            .card-button {
              background: transparent;
              border: 1px solid transparent;
              color: var(--blue-500);
              position: relative;
              transition: all 0.3s ease;

              .sec-label {
                color: var(--base-500);
                
              }
              .sec-icon {
                position: absolute;
                right: 0;
                top: 0;
                bottom: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0;
                transition: all 0.3s ease;
                transform: translateX(0);
                i {
                  color: var(--blue-500);
                }
              }
              &:hover {
                color: white;
                border-color: var(--blue-500);

                padding-right: gr(2);

                .sec-icon {
                  opacity: 1;
                  transform: translateX(-100%);
                  i {
                    color: var(--blue-500);
                  }
                }
                .sec-label {
                  color: var(--blue-500);
                }
              }

              &:active {
                outline: none;
                border: 1px solid var(--cyan-500);
                background: var(--cyan-500);
                >.sec-label {
                  color: white;
                }
                >.sec-icon {
                  i {
                    color: white;
                  }
                }
              }
            }
          }
        }
      }

      &:hover {
        transition: all 0.2s ease;
        @apply shadow-2xl;

        .sec-title {
          >.index {
            color: var(--cyan-500);
          }
          >.title {
            color: var(--base-650);
          }
        }

        .sec-image {

          >.inner {
            transform: translate(-50%, -50%) scale(1.05);
          }
          >.sec-title {
            color: var(--blue-500);
          }
        }

        .sec-content {
          .sec-title {
            .title {
              /* color: var(--blue-800); */
            }
          }


          .sec-description {
            >div {
              color: var(--base-800);

            }
          }

          .sec-actions {
            .action {
              button {
                color: var(--base-800);
                gap: gr(0.75);

                .sec-label {
                  color: var(--blue-600);
                }

                .sec-icon {
                  i {
                    /* color: red; */
                  }
                }
              }
            }
          }
        }
      }

    }
  }

  &:hover {

    transform: scale(1);

    >.inner {
      &.card {
        @apply shadow-xl;

        >.sec-title {
          >.title {
            color: var(--a-dark-blue);
          }
        }
      }
    }
  }
}
</style>